import moment from 'moment'
import pluralize from 'pluralize'
import React, { useState } from 'react'
import { Dimensions, LayoutChangeEvent, View, ViewStyle } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Text } from '@components/base'
import { usePrevious } from '@src/utils/hooks'
import { MultiSlider } from './MultiSlider'

const LOW_VALUE = 0
const HIGH_VALUE = 24
const MARKER_SIZE = 24
const INITIAL_WIDTH = Dimensions.get('screen').width - MARKER_SIZE * 2

const touchDimensions = {
  height: 50,
  width: 80,
  borderRadius: 20,
  slipDisplacement: 40,
}

export interface TimeOfDaySliderProps {
  startValue: number
  endValue: number
  min?: number
  max?: number
  modulo?: number
  onChange: (time: 'startHour' | 'endHour') => (value: number) => void
  containerStyle?: ViewStyle
}

export const TimeOfDaySlider = ({
  endValue = HIGH_VALUE,
  startValue = LOW_VALUE,
  min = LOW_VALUE,
  max = HIGH_VALUE,
  modulo,
  onChange,
  containerStyle = {},
}: TimeOfDaySliderProps) => {
  const themedStyles = useStyleSheet(sliderThemeStyle)
  const [startTime, setStartTime] = useState(startValue)
  const [endTime, setEndTime] = useState(endValue)
  const [sliderLength, setSliderLength] = useState(INITIAL_WIDTH)

  const startEndDiff = endTime - startTime

  const prevStartTime = usePrevious(startTime)
  const prevEndTime = usePrevious(endTime)

  const updateSliderHours = (sliderValues: number[]) => {
    const [start, end] = sliderValues
    if (prevStartTime !== start) {
      setStartTime(start)
    }
    if (prevEndTime !== end) {
      setEndTime(end)
    }
  }

  const handleSliderValueChange = () => {
    if (prevStartTime !== startTime) {
      onChange('startHour')(startTime)
    } else {
      onChange('endHour')(endTime)
    }
  }

  const lowText = moment(modulo ? startTime % modulo : startTime, 'h').format('hh:mm a')
  const highText = moment(modulo ? endTime % modulo : endTime, 'h').format('hh:mm a')

  const onSliderLayout = (event: LayoutChangeEvent) => {
    setSliderLength(event.nativeEvent.layout.width)
  }

  return (
    <View style={containerStyle}>
      <View style={[styles.labelContainer]}>
        <Text type="small" testID="TimeOfDay/Min">
          {lowText}
        </Text>
        <Text type="small">{pluralize('hour', startEndDiff, true)}</Text>
        <Text type="small" testID="TimeOfDay/Max">
          {highText}
        </Text>
      </View>

      <View testID="TimeOfDay/MultiSlider" onLayout={onSliderLayout}>
        <MultiSlider
          min={min}
          max={max}
          step={1}
          values={[startTime, endTime]}
          sliderLength={sliderLength}
          trackStyle={themedStyles.trackStyle}
          markerStyle={themedStyles.marker}
          containerStyle={themedStyles.containerStyle}
          selectedStyle={themedStyles.selectedTrackStyle}
          unselectedStyle={themedStyles.unselectedTrackStyle}
          touchDimensions={touchDimensions}
          stepMarkerStyle={themedStyles.stepMarker}
          onValuesChange={updateSliderHours}
          onValuesChangeFinish={handleSliderValueChange}
        />
      </View>
    </View>
  )
}

const styles = StyleService.create({
  labelContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
})

const sliderThemeStyle = StyleService.create({
  containerStyle: {
    height: 24,
  },
  selectedTrackStyle: {
    backgroundColor: 'theme.primary.base',
  },
  unselectedTrackStyle: {
    backgroundColor: 'theme.surface.base1',
  },
  marker: {
    backgroundColor: 'theme.solid.white',
    marginTop: 4,
    width: MARKER_SIZE,
    height: MARKER_SIZE,
    borderWidth: 1,
    borderColor: 'theme.primary.base',
  },
  trackStyle: {
    height: 4,
    borderRadius: 2,
  },
  stepMarker: {
    backgroundColor: 'theme.background',
    width: 2,
    height: 4,
    borderRadius: 0,
  },
})
